/**
 * DATA.ORIGIN guarantee (D1) — Fibration structure.
 *
 * Neutralizes epistemic lift (G2) by requiring every data value to carry
 * a provenance witness tracing it back to a verified origin.
 *
 * Agda source: Kernel/DATA/ORIGIN/Neutralization.agda
 * Realization fidelity: FULL (branded provenance tags enforced at type level)
 * Safety class: Compositional (innate immunity)
 * Neutralization mode: IMMUNE — violations have no type representation
 * Tier: 2 (structural-geometric)
 * Legacy name: INFO
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { DATA_ORIGIN_META } from "../shared/theorem-meta.js";
export { DATA_ORIGIN_META };

// ── Structure: Fibration ───────────────────────────────────────

/** A verified origin — the source from which data can be trusted. */
export type Origin<O extends string> = Brand<O, "Origin">;

/** Data with provenance — carries a witness of where it came from. */
export interface ProvenancedData<T, O extends string> {
  readonly value: T;
  readonly origin: Origin<O>;
  readonly timestamp: number;
}

/** Create provenanced data from a verified origin. */
export function fromOrigin<T, O extends string>(value: T, origin: O): ProvenancedData<T, O> {
  return {
    value,
    origin: origin as Origin<O>,
    timestamp: Date.now(),
  };
}

/** A violation — data without provenance. */
export interface DataOriginViolation {
  readonly __brand: "DataOriginViolation";
  readonly kind: "unprovenanced-data";
}

/** A DATA.ORIGIN-compliant system: all data carries provenance. */
export interface DataOriginStructure<T, O extends string> {
  readonly verifiedOrigins: readonly O[];
  readonly ingest: (value: T, origin: O) => ProvenancedData<T, O>;
  readonly verify: (data: ProvenancedData<T, O>) => boolean;
}

export function createDataOriginGuarantee<T, O extends string>(
  structure: DataOriginStructure<T, O>,
): Guarantee<DataOriginStructure<T, O>, DataOriginViolation> {
  return {
    structure,
    neutralize: (_violation: DataOriginViolation): never => {
      throw new Error(
        "DATA.ORIGIN violation is structurally inexpressible: " +
          "all data carries provenance witnesses",
      );
    },
    meta: DATA_ORIGIN_META,
  };
}
